import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiClock, FiDatabase, FiCheckCircle, FiAlertTriangle, FiAlertOctagon } from 'react-icons/fi'
import { BsDatabaseFillDown } from 'react-icons/bs'

import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'
import Button from '../../shared/components/Button/Button'
import backupImg from "./img/backup.jpg"
import "./Backup.css"

function daysSince(dateStr) {
    if (!dateStr) return null
    const diffMs = Date.now() - new Date(dateStr).getTime()
    return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

function relativeLabel(days) {
    if (days === null) return 'Nenhum backup foi gerado ainda'
    if (days <= 0) return 'Gerado hoje'
    if (days === 1) return 'Gerado ontem'
    if (days < 30) return `Gerado há ${days} dias`
    const months = Math.floor(days / 30)
    return `Gerado há ${months} ${months === 1 ? 'mês' : 'meses'}`
}

function Backup() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [load, setLoad] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [lastBackup, setLastBackup] = useState({})

    const [myPermission, setMyPermission] = useState({ group: "" })
    const { checkPermission } = useMyPermission()
    const { setFlashMessage } = useFlashMessage()
    const myPermissionCreate = checkPermission(myPermission, "backup_Create")
    const myPermissionView = checkPermission(myPermission, "backup_View")

    async function getLastBkpInfo() {
        api.get(`/backup/getLast`, {
            headers: {
                Authorization: `Bearer ${JSON.parse(token)}`,
            },
        }).then((response) => {
            if (response) {
                setLastBackup(response.data.backup)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => {
        getLastBkpInfo()
    }, [token])

    const handleDownload = async () => {
        const data = new Date()
        const dia = String(data.getDate()).padStart(2, '0')
        const mes = String(data.getMonth() + 1).padStart(2, '0')
        const ano = data.getFullYear()
        const bkpName = `bkp-bestforce-db-${dia}-${mes}-${ano}.gz`

        setDownloading(true)
        try {
            const response = await api.get('/backup/get/backup', {
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${JSON.parse(token)}`,
                },
            })

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', bkpName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            await getLastBkpInfo()
            setFlashMessage('Backup gerado com sucesso!', 'success')
        } catch (error) {
            setFlashMessage('Erro ao gerar o backup.', 'error')
        } finally {
            setDownloading(false)
        }
    }

    const days = useMemo(() => daysSince(lastBackup.date), [lastBackup.date])

    const status = useMemo(() => {
        if (days === null) return { level: 'never', label: 'Nunca gerado', Icon: FiAlertOctagon }
        if (days <= 7) return { level: 'ok', label: 'Em dia', Icon: FiCheckCircle }
        if (days <= 30) return { level: 'warn', label: 'Atenção', Icon: FiAlertTriangle }
        return { level: 'late', label: 'Desatualizado', Icon: FiAlertOctagon }
    }, [days])

    if (!load) {
        return <div className="pg-loading">Carregando...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">
            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Backup</p>
                        <span className="pg-title-accent" />
                    </div>
                </div>
            </div>

            <div className="bkp-wrap">
                <div className="bkp-hero" style={{ backgroundImage: `url(${backupImg})` }}>
                    <div className="bkp-hero-overlay" />
                    <div className="bkp-hero-content">
                        <span className={`bkp-status-badge bkp-status-${status.level}`}>
                            <status.Icon size={14} />
                            {status.label}
                        </span>
                        <p className="bkp-hero-title">Backup do Sistema</p>
                        <p className="bkp-hero-subtitle">
                            Gere uma cópia completa do banco de dados e baixe o arquivo para guardar em segurança.
                        </p>
                        {myPermissionCreate && (
                            <Button
                                variant="new"
                                icon={BsDatabaseFillDown}
                                onClick={handleDownload}
                                disabled={downloading}
                                className="bkp-hero-button"
                            >
                                {downloading ? 'Gerando...' : 'Fazer Backup'}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="bkp-stats">
                    <div className="bkp-stat-card">
                        <div className="bkp-stat-icon bkp-stat-icon-clock"><FiClock size={20} /></div>
                        <div>
                            <p className="bkp-stat-label">Último backup</p>
                            <p className="bkp-stat-value">{lastBackup.name || '--/--/----'}</p>
                            <p className="bkp-stat-sub">{relativeLabel(days)}</p>
                        </div>
                    </div>

                    <div className="bkp-stat-card">
                        <div className={`bkp-stat-icon bkp-stat-icon-${status.level}`}><status.Icon size={20} /></div>
                        <div>
                            <p className="bkp-stat-label">Status</p>
                            <p className="bkp-stat-value">{status.label}</p>
                            <p className="bkp-stat-sub">Recomendado a cada 7 dias</p>
                        </div>
                    </div>

                    <div className="bkp-stat-card">
                        <div className="bkp-stat-icon bkp-stat-icon-db"><FiDatabase size={20} /></div>
                        <div>
                            <p className="bkp-stat-label">Banco de dados</p>
                            <p className="bkp-stat-value">bestforce</p>
                            <p className="bkp-stat-sub">Retenção do histórico: 1 ano</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o backup</p>
        </div>
    )
}

export default Backup
